// The game board: slots plus one persistent element per card (keyed by CardKey, never re-created
// per move), positioned by CSS variables. Exposes the data-* contract the e2e driver reads:
// data-game, data-state, data-seq, data-deal, data-deck/back/background on the root; data-pile,
// data-index, data-face, data-card, data-area on every card.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { type EngineState } from '../engine/controller';
import { CLASSIC_GATHER_STEP_MS, CLASSIC_OFF_BOARD_MS, dealPerCardMsFor, motion } from '../engine/motion';
import { layoutOf, RULES } from '../engine/rules';
import { pyramidPlace } from '../engine/rules/pyramid';
import { useController, useEngineState } from '../store/gameStore';
import { useUiState } from '../store/uiStore';
import { bigCardsProto, emptySlotSrc, tileDeckOf } from '../options/assets';
import { effectsOff } from '../ui/effects';
import { BIG_CARD_MAX_W, CARD_MAX_W, computeGeometry, geometryVars } from './geometry';
import { CardEl, type Area, type CardElProps } from './CardEl';
import { Slots } from './Slots';
import { ConnectTrail } from './ConnectTrail';
import { usePointerInput } from '../input/usePointerInput';
import { hasKey, t } from '../i18n';
import { timelineNow, waitFor, type Wait } from '../ui/compat';
import './board.css';

const WASTE_VISIBLE = 3;
// Every card of the stock, the waste and the foundations is shown (owner 2026-10-16): each lies raised a
// fraction of a pixel above the one under it (board.css --lift), so the real cards make the piles'
// thickness, and a pile thins or grows exactly when a card leaves or lands, in every situation (deal,
// game switch, fast clicks, recycle, undo). A separate thickness drawing could only guess those moments.

/** One render record per card from the engine state. */
/** `sent`: during a deal, how many cards the view has sent from the deck so far (Board's deal sequence);
 *  null = all (no deal running). */
/** `landed`: the classic deal's gather (engine/motion.ts): how many cards have come back onto the stock from off
 *  the board so far, the top one first; null = all (or the modern deal, which gathers them all at once). */
/** `tileType`: Mahjong's tile set picked on the Advanced tab (skin.tileType), else the deck's. */
export function cardRecords(s: EngineState, turn: number | 'spider', deck: string, back: string, sent: number | null = null, landed: number | null = null, tileType?: string): CardElProps[] {
  const out: CardElProps[] = new Array(s.deck.length);
  // Mahjong: the tiles have their own pictures, a set in the style of the deck option (assets.ts tileDeckOf)
  const tiles = RULES[s.game].tiles === true;
  if (tiles) deck = back = tileDeckOf(deck, tileType);
  // Mahjong: a tile's top and left edges with no tile of its own layer beside them, where it steps down to a lower
  // layer or the table; board.css draws its rim there (owner 2026-10-25: a rim between two tiles of one layer looked
  // wrong). From the tiles still on the layout, so a removed tile opens its neighbours' edges.
  const openEdges = (c: number): string | undefined => {
    const places = layoutOf(s).places;
    const p = places[c]!;
    let left = true;
    let top = true;
    places.forEach((q, j) => {
      if (j === c || q.z !== p.z || !s.piles.tableau[j]?.length) return;
      if (q.x === p.x - 2 && Math.abs(q.y - p.y) < 2) left = false;
      if (q.y === p.y - 2 && Math.abs(q.x - p.x) < 2) top = false;
    });
    return `${left ? 'l' : ''}${top ? 't' : ''}` || undefined;
  };
  const gathering = s.status === 'dealing' && s.piles.tableau.every((c) => c.length === 0);
  // Before the first deal (the multiplayer page waits for a game) nothing is on the table.
  const undealt = s.deals === 0 && s.status === 'idle' && s.piles.tableau.every((c) => c.length === 0);
  const base = (key: number, pile: string, area: Area, index: number, col: number, row: number, count: number, hidden: boolean, fanIndex: number, dealIndex: number, pileTop = false): CardElProps => ({
    cardKey: key,
    pile,
    area,
    index,
    col,
    row,
    count,
    z: index + 1,
    faceUp: s.faceUp[key] === true,
    id: s.faceUp[key] ? s.deck[key]!.id : null,
    hidden,
    fanIndex,
    dealIndex,
    deck,
    back,
    pileTop,
  });
  const stock = s.piles.stock;
  stock.forEach((key, i) => {
    const offBoard = gathering && landed !== null && stock.length - 1 - i >= landed;
    out[key] = undealt ? base(key, 'deck', 'deck', i, 0, 0, stock.length, true, 0, 0) : offBoard ? base(key, 'deck', 'offboard', i, 0, 0, stock.length, false, 0, 0) : gathering ? base(key, 'deck', 'deck', i, 0, 0, stock.length, false, 0, 0) : base(key, 'stock', 'stock', i, 0, 0, stock.length, false, 0, 0, i === stock.length - 1); // the top: on whole pixels (board.css)
  });
  const waste = s.piles.waste;
  waste.forEach((key, i) => {
    const fromTop = waste.length - 1 - i; // 0 = top
    // turn three (owner 2026-10-17: a fan on a tall pile "lay unnaturally, it would fall over"): the fan cards
    // (fanIndex 0 = the top, 1, 2) lie spread evenly over the pile, askew (board.css data-fan); the older ones lie
    // square in the pile (-1). The top three always (legacy deck.js updateOpenedFreeCardPos; owner 2026-10-24): when the top card is played
    // the others slide up and the next one comes out from the pile, the fan never shrinks to two or one
    const fanIndex = turn === 3 && fromTop < WASTE_VISIBLE ? fromTop : -1;
    // top: the one card that casts a shadow while cards fly in (board.css)
    out[key] = base(key, 'waste', 'waste', i, 0, 0, waste.length, false, fanIndex, 0, fromTop === 0);
    // the straight spread (board.css): the top three always shown, the top one on the right (legacy); the three under
    // them keep their places while a new turn of three flies in
    if (turn === 3) {
      out[key]!.wasteSlot = fromTop % WASTE_VISIBLE;
      out[key]!.wasteShown = fromTop < WASTE_VISIBLE;
    }
  });
  s.piles.foundations.forEach((pile, f) => {
    pile.forEach((key, i) => {
      const fromTop = pile.length - 1 - i;
      out[key] = base(key, `foundation-${f}`, 'foundation', i, f, 0, pile.length, false, 0, 0, fromTop === 0);
    });
  });
  s.piles.cells.forEach((pile, c) => {
    pile.forEach((key, i) => {
      out[key] = base(key, `cell-${c}`, 'cell', i, c, 0, pile.length, false, 0, 0, true);
    });
  });
  let dealIndex = 0;
  const pairs = RULES[s.game].pairs === true;
  const total = s.piles.tableau.reduce((n, col) => n + col.length, 0);
  const waiting = sent === null ? 0 : Math.max(0, total - sent);
  s.piles.tableau.forEach((col, c) => {
    // the face-down cards at the bottom of the column: they close up first when a long column runs short of room
    // (board.css --step-down)
    const down = col.filter((key) => !s.faceUp[key]).length;
    col.forEach((key, i) => {
      const d = dealIndex++;
      if (sent !== null && d >= sent) {
        // not sent yet: on the deck above the stock's cards, face down, the next one to go on top
        const index = stock.length + (total - 1 - d);
        out[key] = { ...base(key, 'deck', 'deck', index, 0, 0, stock.length + waiting, false, 0, d), faceUp: false, id: null };
      } else {
        // pileTop: the column's top card casts a whole card's flight shadow, the cards above it a strip (board.css)
        // Pyramid: each pile is a place of the pyramid; --row is its row, --col its place in half pitches (board.css)
        const at = pairs && !tiles ? pyramidPlace(c) : null;
        // Mahjong: each pile is a place of the layout; --col and --row in half tiles, --layer its layer; the places in
        // their drawing order (engine/rules/mahjong.ts) give the z-index
        const tile = tiles ? layoutOf(s).places[c]! : null;
        out[key] = tile
          ? { ...base(key, `tableau-${c}`, 'tableau', i, tile.x, tile.y, 1, false, 0, d, true), z: c + 1, layer: tile.z, edges: openEdges(c), down }
          : at
          ? { ...base(key, `tableau-${c}`, 'tableau', i, 2 * at.place - at.row + 6, at.row, 1, false, 0, d, true), down }
          : { ...base(key, `tableau-${c}`, 'tableau', i, c, i, col.length, false, 0, d, i === col.length - 1), down };
      }
    });
  });
  return out;
}

/** The recycle's first step (Board): the card(s) the engine already turned are shown back on top of the
 *  stock, face down (they were its top: turn one = the last waste card, turn three = the last three in
 *  reverse), the waste empty. */
function stagedRecycle(s: EngineState): EngineState {
  const turned = s.piles.waste;
  const faceUp = s.faceUp.slice();
  for (const key of turned) faceUp[key] = false;
  return { ...s, piles: { ...s.piles, stock: [...s.piles.stock, ...[...turned].reverse()], waste: [] }, faceUp };
}

/** A Spider stock deal as a sequence (owner 2026-10-18: the 10 cards flew all at once): the dealt cards not
 *  sent yet (`keys` from `sent` on, in column order) are shown back on top of the stock, face down, the next
 *  one to go on top. A dealt card that is no longer its column's top (a completed run went home at once)
 *  is shown in place. */
function stagedStockDeal(s: EngineState, keys: readonly number[], sent: number): EngineState {
  const waiting = keys.slice(sent).filter((k) => s.piles.tableau.some((col) => col[col.length - 1] === k));
  if (!waiting.length) return s;
  const out = new Set(waiting);
  const faceUp = s.faceUp.slice();
  for (const k of waiting) faceUp[k] = false;
  return {
    ...s,
    piles: { ...s.piles, stock: [...s.piles.stock, ...[...waiting].reverse()], tableau: s.piles.tableau.map((col) => col.filter((k) => !out.has(k))) },
    faceUp,
  };
}
/** Its undo (owner 2026-10-18: "przy undo też powinny wracać pojedynczo"): the cards not sent back yet stay on
 *  their columns, face up, the last one dealt going first. `cols`: each card's column before the undo. */
function stagedStockUndo(s: EngineState, keys: readonly number[], cols: ReadonlyMap<number, number>, sent: number): EngineState {
  const waiting = keys.slice(sent).filter((k) => s.piles.stock.includes(k) && cols.has(k));
  if (!waiting.length) return s;
  const out = new Set(waiting);
  const faceUp = s.faceUp.slice();
  const tableau = s.piles.tableau.map((col) => col.slice());
  for (const k of waiting) {
    faceUp[k] = true;
    tableau[cols.get(k)!]!.push(k);
  }
  return { ...s, piles: { ...s.piles, stock: s.piles.stock.filter((k) => !out.has(k)), tableau }, faceUp };
}
/** How long the stock deal sequence lasts: the last card sent at 9 x 70 ms, its flight (360 ms to a column,
 *  400 back onto the stock), a little. */
const stockDealMs = (undo: boolean) => {
  const m = motion();
  // classic: a dealt card turns over after it lands (legacy spider.js)
  return m.spiderPerCardMs * 9 + (undo ? m.flight.stock : m.dealFlightMs + (m.classic ? m.turnMs : 0)) + 40;
};

export function Board() {
  const s = useEngineState();
  const ctrl = useController();
  const ui = useUiState();
  // the turn-three waste lies straight: the fan switched off in Options, or the big-sign deck (`_v2`; owner
  // 2026-10-24); small cards make it straight by themselves (geometry.ts STRAIGHT_FAN_MAX_W)
  const bigSigns = ui.options.skin.deckType.endsWith('_v2');
  const straightWaste = !ui.options.option.wasteFan || bigSigns;
  // Options "Column spacing" (owner 2026-10-24); not chosen: narrow in the Classic mode
  const columns = ui.options.option.columns;
  const rules = RULES[s.game];
  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // Geometry: measured with a ResizeObserver, written as CSS variables (no per-card measuring).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return;
    const apply = () => {
      // the anchor ad's band over the board's bottom (the page grid no longer makes room for it, app.css)
      const ad = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ad-anchor-h')) || 0;
      const overlap = Math.max(0, root.getBoundingClientRect().bottom - (innerHeight - ad));
      const g = computeGeometry(s.game, root.clientWidth, root.clientHeight, ad > 0 ? overlap : 0, bigCardsProto() ? BIG_CARD_MAX_W : CARD_MAX_W, effectsOff(), straightWaste, bigSigns, columns === 'auto' ? effectsOff() : columns === 'narrow', s.layout);
      for (const [k, v] of Object.entries(geometryVars(g))) inner.style.setProperty(k, v);
      // the side layout of a very low board (geometry.ts SIDE_LAYOUT_MAX_H), for the tests
      if (g.side) inner.dataset.layout = 'side';
      else delete inner.dataset.layout;
      // small cards: the turn-three waste lies straight (geometry.ts STRAIGHT_FAN_MAX_W, board.css)
      if (g.straightFan) inner.dataset.fan = 'straight';
      else delete inner.dataset.fan;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(root);
    // the ad band comes, changes and goes without a resize: AdAnchorBand writes it on <html style>; the Classic
    // mode switches on <html data-effects> (its own column spacing)
    const mo = new MutationObserver(apply);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-effects'] });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [s.game, s.layout, straightWaste, bigSigns, columns]);

  usePointerInput(innerRef, ctrl);

  // Win: the cards stay on the foundations under the win window (owner 2026-10-17: the legacy game-over
  // animation, the cards falling off the board, is gone).

  // The recycle as a sequence (owner 2026-10-16): the engine moves the waste back onto the stock AND turns
  // the first card(s) in one step. The view first shows the turned card(s) back on the stock with the rest
  // (the whole waste glides back and turns face down), then, once they have landed, the real state (the
  // first card turns over onto the waste). Any next action ends the sequence at once (the next seq), so
  // fast clicks just send every card straight to its place. data-state is "busy" meanwhile (the tests
  // wait for the board to settle). The wait is a document-timeline animation, which runs in real time
  // like the card transitions (also under a frozen page.clock).
  const [seen, setSeen] = useState({
    seq: s.seq,
    recycles: s.recycles,
    staged: -1,
    stock: s.piles.stock,
    tableau: s.piles.tableau,
    faceUp: s.faceUp,
    flipFirst: -1,
    found: s.piles.foundations,
    runHome: -1,
    stockDeal: null as null | { seq: number; keys: number[]; undo: boolean; cols: Map<number, number> },
  });
  if (seen.seq !== s.seq) {
    const recycled = s.recycles > seen.recycles && s.piles.waste.length > 0 && s.status !== 'dealing';
    // a Spider stock deal: the stock lost its top cards to the columns while the engine stays busy
    let stockDeal = seen.stockDeal;
    const spiderBusy = rules.turn === 'spider' && s.status === 'busy' && s.deals > 0;
    if (spiderBusy && s.piles.stock.length < seen.stock.length) {
      const dealt = seen.stock.slice(s.piles.stock.length);
      const column = (k: number) => s.piles.tableau.findIndex((col) => col.includes(k));
      stockDeal = { seq: s.seq, keys: dealt.filter((k) => column(k) >= 0).sort((a, b) => column(a) - column(b)), undo: false, cols: new Map() };
    } else if (spiderBusy && s.piles.stock.length > seen.stock.length) {
      // the deal undone: the cards go back from the last column to the first
      const back = s.piles.stock.slice(seen.stock.length);
      const cols = new Map<number, number>();
      for (const k of back) {
        const c = seen.tableau.findIndex((col) => col[col.length - 1] === k);
        if (c >= 0) cols.set(k, c);
      }
      stockDeal = { seq: s.seq, keys: back.filter((k) => cols.has(k)).sort((a, b) => cols.get(b)! - cols.get(a)!), undo: true, cols };
    } else if (s.status !== 'busy') stockDeal = null; // a new deal, a game switch...
    // An undo that turns a card back face down where it lies (the one a move had uncovered): the turn starts
    // first and the returning cards follow near its end (board.css, CardEl UNDO_TURN_MS) (owner 2026-10-18: both at once looked out of
    // step). Only a card that stays in its place turning face down marks it; a gather or a recycle moves it.
    // classic: both at once, like legacy (klondike.js undo)
    const flipFirst =
      !motion().classic &&
      s.status !== 'dealing' &&
      s.piles.tableau.some((col, c) => col.some((k, i) => !s.faceUp[k] && seen.faceUp[k] === true && seen.tableau[c]?.[i] === k));
    // a completed Spider run going home (classic: one card every 70 ms, the Ace first, board.css data-run-home)
    const runHome = rules.turn === 'spider' && s.piles.foundations.some((f, i) => f.length >= 13 && (seen.found[i]?.length ?? 0) === 0);
    setSeen({ seq: s.seq, recycles: s.recycles, staged: recycled ? s.seq : -1, stock: s.piles.stock, tableau: s.piles.tableau, faceUp: s.faceUp, flipFirst: flipFirst ? s.seq : -1, found: s.piles.foundations, runHome: runHome ? s.seq : -1, stockDeal });
  }
  const staging = seen.staged === s.seq;
  const runHome = seen.runHome === s.seq;
  const flipFirst = seen.flipFirst === s.seq;
  // the mark lives only for that update's glide (a snap-back after a drag must not wait)
  useEffect(() => {
    if (seen.flipFirst < 0) return;
    const marked = seen.flipFirst;
    const wait = waitFor(300);
    void wait.finished.then(
      () => setSeen((v) => (v.flipFirst === marked ? { ...v, flipFirst: -1 } : v)),
      () => undefined,
    );
    return () => wait.cancel();
  }, [seen.flipFirst]);
  useEffect(() => {
    if (seen.staged < 0) return;
    const staged = seen.staged;
    const wait = waitFor(motion().recycleStageMs);
    void wait.finished.then(
      () => setSeen((v) => (v.staged === staged ? { ...v, staged: -1 } : v)),
      () => undefined,
    );
    return () => wait.cancel();
  }, [seen.staged]);

  // The deal as a sequence (owner 2026-10-16): the dealt cards stay on the deck, the next one on top, and
  // every DEAL_PER_CARD_MS the top one is sent to its column, so the deck thins from the top and every card
  // flies above the ones sent before it (a CSS transition-delay left them all at the deck's top place and
  // sent the first from the top but under the later ones). `sent` counts on a document-timeline animation
  // (real time, like the card transitions, also under a frozen page.clock); the engine's own deal timer
  // (30 ms a card + 300 ms) ends `dealing`, and then every card is shown in place at once.
  const dealtPhase = s.status === 'dealing' && s.piles.tableau.some((c) => c.length > 0);
  // classic (engine/motion.ts): every dealt card leaves at once (legacy deck.js deal)
  const classic = motion().classic;
  const [sent, setSent] = useState<{ deal: number; n: number }>({ deal: -1, n: 0 });
  useEffect(() => {
    if (!dealtPhase || classic) return;
    const deal = s.deals;
    const total = s.piles.tableau.reduce((n, col) => n + col.length, 0);
    const perCard = dealPerCardMsFor(RULES[s.game].tiles === true);
    // n follows the elapsed time (a tick resolves a frame late, counting ticks fell behind the engine)
    const t0 = timelineNow();
    let stopped = false;
    let tick: Wait | null = null;
    const step = () => {
      if (stopped) return;
      const n = Math.min(total, Math.floor((timelineNow() - t0) / perCard) + 1);
      setSent({ deal, n });
      if (n >= total) return;
      tick = waitFor(perCard / 2);
      void tick.finished.then(step, () => undefined);
    };
    step();
    return () => {
      stopped = true;
      tick?.cancel();
    };
  }, [dealtPhase, classic, s.deals, s.piles.tableau]);
  const dealSent = dealtPhase && !classic ? (sent.deal === s.deals ? sent.n : 0) : null;

  // The classic gather (legacy deck.js shuffle + deal): the cards fly off the board's top left corner, wait
  // there, and come back onto the stock one every 8 ms, the top one first; the engine's gather phase lasts
  // exactly that long (motion.ts dealGatherMs). Counted on the document timeline like the modern deal.
  const gatherPhase = classic && s.status === 'dealing' && s.piles.tableau.every((c) => c.length === 0) && s.deals >= 0;
  const [landed, setLanded] = useState<{ seq: number; n: number }>({ seq: -1, n: 0 });
  const gatherSeq = gatherPhase ? s.seq : -1;
  useEffect(() => {
    if (gatherSeq < 0) return;
    const total = s.piles.stock.length;
    const t0 = timelineNow();
    let stopped = false;
    let tick: Wait | null = null;
    const step = () => {
      if (stopped) return;
      const e = timelineNow() - t0;
      const n = e < CLASSIC_OFF_BOARD_MS ? 0 : Math.min(total, Math.floor((e - CLASSIC_OFF_BOARD_MS) / CLASSIC_GATHER_STEP_MS) + 1);
      setLanded({ seq: gatherSeq, n });
      if (n >= total) return;
      tick = waitFor(e < CLASSIC_OFF_BOARD_MS ? CLASSIC_OFF_BOARD_MS - e : CLASSIC_GATHER_STEP_MS);
      void tick.finished.then(step, () => undefined);
    };
    step();
    return () => {
      stopped = true;
      tick?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatherSeq]);
  // where the board's top is on the screen: on a touch screen the cards fly fully off the screen (board.css)
  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (gatherSeq >= 0 && inner) inner.style.setProperty('--board-top', `${Math.max(0, inner.getBoundingClientRect().top)}px`);
  }, [gatherSeq]);
  const gatherLanded = gatherPhase ? (landed.seq === s.seq ? landed.n : 0) : null;

  // The Spider stock deal's sequence: one card every SPIDER_STOCK_PER_CARD_MS on the document timeline (real
  // time, like the first deal's), the board marked `data-stock-deal` until the last one has landed (board.css:
  // the 360 ms flight, the card sent last on top; CardEl: the turn fits the flight).
  const stockDealSeq = seen.stockDeal?.seq ?? -1;
  const stockDealTotal = seen.stockDeal ? stockDealMs(seen.stockDeal.undo) : 0;
  const [stockSent, setStockSent] = useState<{ seq: number; n: number }>({ seq: -1, n: 0 });
  useEffect(() => {
    if (stockDealSeq < 0) return;
    const t0 = timelineNow();
    let stopped = false;
    let tick: Wait | null = null;
    const step = () => {
      if (stopped) return;
      const elapsed = timelineNow() - t0;
      if (elapsed >= stockDealTotal) {
        setSeen((v) => (v.stockDeal?.seq === stockDealSeq ? { ...v, stockDeal: null } : v));
        return;
      }
      setStockSent({ seq: stockDealSeq, n: Math.floor(elapsed / motion().spiderPerCardMs) + 1 });
      tick = waitFor(motion().spiderPerCardMs / 2);
      void tick.finished.then(step, () => undefined);
    };
    step();
    return () => {
      stopped = true;
      tick?.cancel();
    };
  }, [stockDealSeq, stockDealTotal]);
  const stockDeal = seen.stockDeal;
  const stockN = stockDeal ? (stockSent.seq === stockDeal.seq ? stockSent.n : 0) : null;

  const skin = ui.options.skin;
  const records = useMemo(
    () =>
      cardRecords(
        staging
          ? stagedRecycle(s)
          : stockDeal && stockN !== null
            ? stockDeal.undo
              ? stagedStockUndo(s, stockDeal.keys, stockDeal.cols, stockN)
              : stagedStockDeal(s, stockDeal.keys, stockN)
            : s,
        rules.turn,
        skin.deckType,
        skin.cardBackType,
        dealSent,
        gatherLanded,
        skin.tileType,
      ),
    [s, staging, rules.turn, skin.deckType, skin.cardBackType, skin.tileType, dealSent, gatherLanded, stockDeal, stockN],
  );
  const innerStyle = { '--empty-img': `url("${emptySlotSrc(skin.backgroundType)}")` } as CSSProperties;

  return (
    <div
      id="main"
      ref={rootRef}
      className="board"
      data-board=""
      data-game={s.game}
      data-tiles={RULES[s.game].tiles ? '' : undefined}
      data-layout={RULES[s.game].tiles ? layoutOf(s).name : undefined}
      data-state={staging || (s.undoWaiting && s.status === 'idle') ? 'busy' : s.status}
      data-seq={s.seq}
      data-stock-deal={stockDeal ? '' : undefined}
      data-flip-first={flipFirst ? '' : undefined}
      data-recycling={staging ? '' : undefined}
      data-run-home={runHome ? '' : undefined}
      data-deal={s.deals}
      data-deck={skin.deckType}
      data-back={skin.cardBackType}
      data-background={skin.backgroundType}
    >
      <div ref={innerRef} className="board__inner" style={innerStyle}>
        <Slots foundations={rules.foundations} columns={rules.tiles ? 0 : rules.columns.length} cells={rules.cells} />
        {rules.maxRecycles !== undefined && (s.deals > 0 || s.status === 'dealing') ? (
          // Pyramid: which pass through the stock this is (owner 2026-10-24: "dodaj licznik"), under the stock
          <div className="pass-count" data-pass={s.recycles + 1}>
            {hasKey('pyramid.pass') ? t('pyramid.pass', { n: s.recycles + 1, max: rules.maxRecycles + 1 }) : `${s.recycles + 1}/${rules.maxRecycles + 1}`}
          </div>
        ) : null}
        {records.map((r) => (
          <CardEl key={r.cardKey} {...r} />
        ))}
        {s.game === 'mahjong-connect' ? <ConnectTrail innerRef={innerRef} /> : null}
      </div>
    </div>
  );
}
